export type MediaKind = 'image' | 'video';

export type MediaDerivativeKind =
  | 'thumbnail'
  | 'smallPreview'
  | 'preview'
  | 'largePreview'
  | 'poster';

export type MediaObject = {
  key: string | null;
  url: string;
  mimeType: string | null;
  bytes: number | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** Image-only source metadata populated by the derivative processor when known. */
  hasTransparentPixels?: boolean | null;
};

export type MediaDerivative = MediaObject & {
  kind: MediaDerivativeKind;
};

export type MediaAsset = {
  id: string;
  kind: MediaKind;
  original: MediaObject;
  derivatives: Partial<Record<MediaDerivativeKind, MediaDerivative>>;
  derivativePolicyVersion?: number | null;
};

export const MEDIA_ORIGINAL_PREVIEW_MAX_BYTES = 90 * 1024 * 1024;
export const MEDIA_ORIGINAL_PREVIEW_MAX_PIXELS = 100_000_000;

type LegacyMediaAssetInput = {
  id: string;
  kind: MediaKind;
  url: string;
  key?: string | null;
  mimeType?: string | null;
  bytes?: number | null;
  width?: number | null;
  height?: number | null;
  durationMs?: number | null;
};

function normalizeNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function normalizePositiveDimension(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;
}

function normalizePositiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1
    ? value
    : null;
}

function normalizeMediaObject(value: unknown): MediaObject | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const url = typeof record.url === 'string' ? record.url.trim() : '';
  if (!url) return null;

  return {
    key:
      typeof record.key === 'string' && record.key.trim()
        ? record.key.trim()
        : null,
    url,
    mimeType:
      typeof record.mimeType === 'string' && record.mimeType.trim()
        ? record.mimeType.trim()
        : null,
    bytes: normalizeNullableNumber(record.bytes),
    width: normalizePositiveDimension(record.width),
    height: normalizePositiveDimension(record.height),
    durationMs: normalizeNullableNumber(record.durationMs),
    hasTransparentPixels:
      typeof record.hasTransparentPixels === 'boolean'
        ? record.hasTransparentPixels
        : null,
  };
}

export function createLegacyMediaAsset(
  input: LegacyMediaAssetInput
): MediaAsset {
  return {
    id: input.id,
    kind: input.kind,
    original: {
      key: input.key?.trim() || null,
      url: input.url.trim(),
      mimeType: input.mimeType?.trim() || null,
      bytes: normalizeNullableNumber(input.bytes),
      width: normalizePositiveDimension(input.width),
      height: normalizePositiveDimension(input.height),
      durationMs: normalizeNullableNumber(input.durationMs),
    },
    derivatives: {},
    derivativePolicyVersion: null,
  };
}

export function normalizeMediaAsset(value: unknown): MediaAsset | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id.trim() : '';
  const kind =
    record.kind === 'image' || record.kind === 'video' ? record.kind : null;
  const original = normalizeMediaObject(record.original);
  if (!id || !kind || !original) return null;

  const derivatives: MediaAsset['derivatives'] = {};
  const rawDerivatives =
    record.derivatives &&
    typeof record.derivatives === 'object' &&
    !Array.isArray(record.derivatives)
      ? (record.derivatives as Record<string, unknown>)
      : {};

  for (const derivativeKind of [
    'thumbnail',
    'smallPreview',
    'preview',
    'largePreview',
    'poster',
  ] as const) {
    const object = normalizeMediaObject(rawDerivatives[derivativeKind]);
    if (object) {
      derivatives[derivativeKind] = {
        kind: derivativeKind,
        ...object,
      };
    }
  }

  return {
    id,
    kind,
    original,
    derivatives,
    derivativePolicyVersion: normalizePositiveInteger(
      record.derivativePolicyVersion
    ),
  };
}

export function normalizeMediaAssets(value: unknown): MediaAsset[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const asset = normalizeMediaAsset(entry);
    return asset ? [asset] : [];
  });
}

export function createLegacyMediaAssets(params: {
  images: string[];
  videos: string[];
}): MediaAsset[] {
  return [
    ...params.images.map((url, index) =>
      createLegacyMediaAsset({
        id: `image-${index}`,
        kind: 'image',
        url,
      })
    ),
    ...params.videos.map((url, index) =>
      createLegacyMediaAsset({
        id: `video-${index}`,
        kind: 'video',
        url,
      })
    ),
  ];
}

export function isMediaAssetDerivativeComplete(asset: MediaAsset) {
  if (asset.kind === 'video') {
    return Boolean(
      asset.derivatives.poster?.key &&
        asset.derivatives.thumbnail?.key &&
        asset.original.width &&
        asset.original.height &&
        asset.original.durationMs
    );
  }

  return Boolean(
    asset.original.width &&
      asset.original.height &&
      asset.derivatives.thumbnail?.key &&
      asset.derivatives.smallPreview?.key &&
      asset.derivatives.preview?.key &&
      asset.derivatives.largePreview?.key
  );
}

export function canUseMediaOriginalAsPreviewFallback(asset: MediaAsset) {
  if (asset.kind !== 'image') return false;

  const { bytes, width, height } = asset.original;
  if (bytes === null || bytes > MEDIA_ORIGINAL_PREVIEW_MAX_BYTES) {
    return false;
  }

  if (
    width !== null &&
    height !== null &&
    width * height > MEDIA_ORIGINAL_PREVIEW_MAX_PIXELS
  ) {
    return false;
  }

  // Persisted provider outputs are size-bounded before they reach R2, so a
  // known-small original can safely use wsrv even before derivative metadata
  // exists. Legacy/keyless media still needs known dimensions before fallback.
  if ((width === null || height === null) && !asset.original.key) {
    return false;
  }

  return true;
}

export function resolveMediaAssetDisplayObject(
  asset: MediaAsset,
  intent: 'thumbnail' | 'preview' | 'largePreview' | 'download'
): MediaObject {
  if (intent === 'download') return asset.original;

  if (asset.kind === 'video') {
    return intent === 'thumbnail'
      ? (asset.derivatives.thumbnail ??
          asset.derivatives.poster ??
          asset.original)
      : (asset.derivatives.poster ??
          asset.derivatives.thumbnail ??
          asset.original);
  }

  if (intent === 'thumbnail') {
    return (
      asset.derivatives.thumbnail ??
      asset.derivatives.smallPreview ??
      asset.derivatives.preview ??
      asset.derivatives.largePreview ??
      asset.original
    );
  }

  if (intent === 'largePreview') {
    return (
      asset.derivatives.largePreview ??
      asset.derivatives.preview ??
      asset.derivatives.smallPreview ??
      asset.derivatives.thumbnail ??
      asset.original
    );
  }

  return (
    asset.derivatives.preview ??
    asset.derivatives.smallPreview ??
    asset.derivatives.largePreview ??
    asset.derivatives.thumbnail ??
    asset.original
  );
}

export function resolveMediaAssetStaticPreviewObject(
  asset: MediaAsset,
  intent: 'thumbnail' | 'preview' | 'largePreview' = 'thumbnail'
): MediaObject | null {
  if (asset.kind === 'video') {
    return intent === 'thumbnail'
      ? (asset.derivatives.thumbnail ?? asset.derivatives.poster ?? null)
      : (asset.derivatives.poster ?? asset.derivatives.thumbnail ?? null);
  }

  const preview = resolveMediaAssetDisplayObject(asset, intent);
  if (
    preview.url === asset.original.url &&
    !canUseMediaOriginalAsPreviewFallback(asset)
  ) {
    return null;
  }
  return preview;
}

export function resolveMediaAssetAspectRatio(asset: MediaAsset): number | null {
  const display = resolveMediaAssetDisplayObject(asset, 'preview');
  const width = display.width ?? asset.original.width;
  const height = display.height ?? asset.original.height;
  if (!width || !height) return null;
  return width / height;
}
